import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
function fmtHkd(value: unknown): string {
  const n = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (!Number.isFinite(n)) return 'HK$0.00';
  return `HK$${n.toLocaleString('zh-HK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
import {
  BarChart3, Calendar, TrendingUp, Users, ShoppingCart, CreditCard,
  MessageCircle, Share2, Ticket, Smartphone, ArrowUpRight, Loader2, AlertCircle
} from 'lucide-react';

type Preset = 'today' | '7d' | '30d' | 'custom';

interface AnalyticsData {
  period?: { start?: number; end?: number };
  kpis?: {
    orders?: number;
    revenue?: string | number;
    aov?: string | number;
    pageViews?: number;
    uniqueVisitors?: number;
    whatsappInbound?: number;
    whatsappOutbound?: number;
    referralClicks?: number;
    pendingPayments?: number;
  };
  funnel?: {
    visitors?: number;
    productViews?: number;
    addToCarts?: number;
    beginCheckouts?: number;
    orders?: number;
    paidOrders?: number;
    conversionRates?: Record<string, string>;
  };
  trend?: Array<{
    date?: string;
    orders?: number;
    revenue?: string | number;
    pageViews?: number;
  }>;
  customerSegments?: Array<{
    segment?: string;
    count?: number;
    revenue?: string | number;
  }>;
  referrals?: {
    totalClicks?: number;
    ordersFromReferral?: number;
    revenueFromReferral?: string | number;
    newReferrers?: number;
    topReferrers?: Array<{
      name?: string;
      code?: string;
      orders?: number;
      revenue?: string | number;
    }>;
  };
  whatsapp?: {
    inbound?: number;
    outbound?: number;
    autoReplies?: number;
    keywordTriggers?: Record<string, number>;
  };
  offers?: Array<{
    key?: string;
    title?: string;
    claims?: number;
    redeemed?: number;
  }>;
}

const PRESETS: { key: Preset; label: string }[] = [
  { key: 'today', label: '今日' },
  { key: '7d', label: '近 7 天' },
  { key: '30d', label: '近 30 天' },
  { key: 'custom', label: '自訂' },
];

function toDateInputValue(d: Date): string {
  const offset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offset).toISOString().split('T')[0];
}

function startOfDay(d: Date): Date {
  const nd = new Date(d);
  nd.setHours(0, 0, 0, 0);
  return nd;
}

function endOfDay(d: Date): Date {
  const nd = new Date(d);
  nd.setHours(23, 59, 59, 999);
  return nd;
}

function getPresetRange(preset: Preset, customStart?: string, customEnd?: string): { start: number; end: number } {
  const now = new Date();
  if (preset === 'today') {
    return { start: Math.floor(startOfDay(now).getTime() / 1000), end: Math.floor(now.getTime() / 1000) };
  }
  if (preset === '7d' || preset === '30d') {
    const days = preset === '7d' ? 6 : 29;
    const start = new Date(now);
    start.setDate(start.getDate() - days);
    return { start: Math.floor(startOfDay(start).getTime() / 1000), end: Math.floor(now.getTime() / 1000) };
  }
  const s = customStart ? new Date(customStart) : now;
  const e = customEnd ? new Date(customEnd) : now;
  return { start: Math.floor(startOfDay(s).getTime() / 1000), end: Math.floor(endOfDay(e).getTime() / 1000) };
}

function fmtDateRange(start?: number, end?: number): string {
  if (!start || !end) return '-';
  return `${new Date(start * 1000).toLocaleDateString('zh-HK')} - ${new Date(end * 1000).toLocaleDateString('zh-HK')}`;
}

function safeNum(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function safePct(numerator: number, denominator: number): string {
  return denominator > 0 ? ((numerator / denominator) * 100).toFixed(2) + '%' : '0.00%';
}

const SEGMENT_COLORS: Record<string, string> = {
  新客: 'bg-blue-500',
  回頭客: 'bg-green-500',
  沈默新客: 'bg-amber-500',
  超級顧客: 'bg-red-500',
};

const AdminAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const [preset, setPreset] = useState<Preset>('7d');
  const [customStart, setCustomStart] = useState<string>(toDateInputValue(new Date()));
  const [customEnd, setCustomEnd] = useState<string>(toDateInputValue(new Date()));
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const range = useMemo(() => getPresetRange(preset, customStart, customEnd), [preset, customStart, customEnd]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/admin/analytics/v2?startDate=${range.start}&endDate=${range.end}`);
      if (res.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json.data ?? {});
    } catch (e: any) {
      setError(e.message || '載入數據失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchAnalytics();
  }, [range.start, range.end]);

  const kpis = data?.kpis ?? {};
  const funnel = data?.funnel ?? {};
  const trend = data?.trend ?? [];
  const segments = data?.customerSegments ?? [];
  const referrals = data?.referrals ?? {};
  const whatsapp = data?.whatsapp ?? {};
  const offers = data?.offers ?? [];

  const kpiCards = [
    { label: '訂單數', value: kpis.orders ?? 0, icon: ShoppingCart, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20' },
    { label: '營收', value: fmtHkd(kpis.revenue), icon: CreditCard, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20' },
    { label: '瀏覽量', value: kpis.pageViews ?? 0, icon: TrendingUp, color: 'text-indigo-600', bg: 'bg-indigo-50 dark:bg-indigo-900/20' },
    { label: '獨立訪客', value: kpis.uniqueVisitors ?? 0, icon: Users, color: 'text-purple-600', bg: 'bg-purple-50 dark:bg-purple-900/20' },
    { label: 'WhatsApp 對話', value: (kpis.whatsappInbound ?? 0) + (kpis.whatsappOutbound ?? 0), icon: MessageCircle, color: 'text-teal-600', bg: 'bg-teal-50 dark:bg-teal-900/20' },
    { label: '推薦點擊', value: kpis.referralClicks ?? 0, icon: Share2, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/20' },
  ];

  const funnelSteps = [
    { key: 'visitors', label: '獨立訪客', value: safeNum(funnel.visitors) },
    { key: 'productViews', label: '商品頁', value: safeNum(funnel.productViews) },
    { key: 'addToCarts', label: '購物車', value: safeNum(funnel.addToCarts) },
    { key: 'beginCheckouts', label: '結帳', value: safeNum(funnel.beginCheckouts) },
    { key: 'orders', label: '下單', value: safeNum(funnel.orders) },
    { key: 'paidOrders', label: '付款', value: safeNum(funnel.paidOrders) },
  ];
  const funnelMax = Math.max(1, ...funnelSteps.map(s => s.value));

  const maxTrendRevenue = Math.max(1, ...trend.map(d => safeNum(d.revenue)));
  const maxTrendOrders = Math.max(1, ...trend.map(d => safeNum(d.orders)));

  const renderValue = (value: unknown, formatter?: (v: number) => string) => {
    if (value === undefined || value === null) return '-';
    if (typeof value === 'number') return formatter ? formatter(value) : value.toLocaleString('zh-HK');
    return String(value);
  };

  return (
    <AdminLayout currentPage="analytics">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-red-600" />
              數據分析
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {fmtDateRange(data?.period?.start, data?.period?.end)}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {PRESETS.map(p => (
              <button
                key={p.key}
                onClick={() => setPreset(p.key)}
                className={`px-3 py-1.5 rounded-lg text-sm border transition ${
                  preset === p.key
                    ? 'bg-red-600 text-white border-red-600'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {p.label}
              </button>
            ))}
            {preset === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-white"
                />
                <span className="text-gray-500">-</span>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-white"
                />
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 flex items-center gap-2">
            <AlertCircle size={18} />
            {error}
          </div>
        )}

        {loading && (
          <div className="py-12 text-center text-gray-500 dark:text-gray-400">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
            載入中...
          </div>
        )}

        {!loading && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              {kpiCards.map((card, idx) => (
                <div key={idx} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                  <div className={`w-9 h-9 rounded-lg ${card.bg} flex items-center justify-center mb-3`}>
                    <card.icon className={`w-5 h-5 ${card.color}`} />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{card.label}</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{card.value}</p>
                </div>
              ))}
            </div>

            {/* Funnel + Trend */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Conversion Funnel */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-red-600" /> 轉化漏斗
                </h3>
                <div className="space-y-3">
                  {funnelSteps.map((step, idx) => {
                    const prev = idx > 0 ? funnelSteps[idx - 1].value : 0;
                    const rate = prev > 0 ? safePct(step.value, prev) : '100.00%';
                    return (
                      <div key={step.key}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600 dark:text-gray-400">{step.label}</span>
                          <span className="font-bold text-gray-800 dark:text-white">{step.value.toLocaleString('zh-HK')}</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                          <div
                            className="bg-gradient-to-r from-red-500 to-red-400 h-3 rounded-full transition-all"
                            style={{ width: `${(step.value / funnelMax) * 100}%` }}
                          />
                        </div>
                        {idx > 0 && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">轉化率 {rate}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">訪客到下單</span>
                    <span className="font-bold text-gray-800 dark:text-white">{funnel.conversionRates?.visitorToOrder ?? safePct(safeNum(funnel.orders), safeNum(funnel.visitors))}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-2">
                    <span className="text-gray-500 dark:text-gray-400">購物車到結帳</span>
                    <span className="font-bold text-gray-800 dark:text-white">{funnel.conversionRates?.cartToCheckout ?? safePct(safeNum(funnel.beginCheckouts), safeNum(funnel.addToCarts))}</span>
                  </div>
                </div>
              </div>

              {/* Revenue & Order Trend */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-red-600" /> 營收與訂單趨勢
                </h3>
                {trend.length === 0 ? (
                  <p className="text-gray-400 dark:text-gray-500 text-center py-8">暫無趨勢數據</p>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="min-w-[500px] h-56 flex items-end gap-2">
                      {trend.map((day) => {
                        const rev = safeNum(day.revenue);
                        const orders = safeNum(day.orders);
                        return (
                          <div key={day.date || Math.random()} className="flex-1 flex flex-col items-center gap-1">
                            <div className="relative w-full flex items-end justify-center h-40">
                              <div
                                className="w-3/5 bg-red-500/80 dark:bg-red-600 rounded-t transition-all"
                                style={{ height: `${(rev / maxTrendRevenue) * 100}%`, minHeight: rev > 0 ? '4px' : '0' }}
                                title={`營收 ${fmtHkd(rev)}`}
                              />
                              <div
                                className="absolute -top-5 text-[10px] text-gray-700 dark:text-gray-300 font-bold"
                                style={{ opacity: orders > 0 ? 1 : 0 }}
                              >
                                {orders > 0 ? `${orders}` : ''}
                              </div>
                            </div>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400 rotate-45 origin-left translate-y-3">
                              {(day.date || '').slice(5)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-4 mt-6 text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500/80 dark:bg-red-600 rounded" /> 營收</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-gray-200 dark:bg-gray-700 rounded" /> 數字標註為訂單數</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Customer Segments + Referrals */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Customer Segments */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-red-600" /> 顧客分層
                </h3>
                {segments.length === 0 ? (
                  <p className="text-gray-400 dark:text-gray-500 text-center py-8">暫無數據</p>
                ) : (
                  <div className="space-y-3">
                    {segments.map((seg) => {
                      const count = safeNum(seg.count);
                      const maxCount = Math.max(1, ...segments.map(s => safeNum(s.count)));
                      return (
                        <div key={seg.segment || 'unknown'}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600 dark:text-gray-400">{seg.segment || '未知'}</span>
                            <span className="font-bold text-gray-800 dark:text-white">{count.toLocaleString('zh-HK')} 人 · {fmtHkd(seg.revenue)}</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                            <div className={`${SEGMENT_COLORS[seg.segment || ''] || 'bg-gray-500'} h-2.5 rounded-full`} style={{ width: `${(count / maxCount) * 100}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Referral Network */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-red-600" /> 推薦網絡
                </h3>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-gray-800 dark:text-white">{(referrals.totalClicks ?? 0).toLocaleString('zh-HK')}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">連結點擊</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-gray-800 dark:text-white">{(referrals.newReferrers ?? 0).toLocaleString('zh-HK')}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">新推薦人</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-gray-800 dark:text-white">{(referrals.ordersFromReferral ?? 0).toLocaleString('zh-HK')}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">推薦訂單</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-gray-800 dark:text-white">{fmtHkd(referrals.revenueFromReferral)}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">推薦營收</p>
                  </div>
                </div>
                <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Top 10 推薦人</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {(referrals.topReferrers ?? []).length === 0 ? (
                    <p className="text-gray-400 dark:text-gray-500 text-sm">暫無數據</p>
                  ) : (
                    (referrals.topReferrers ?? []).map((r, idx) => (
                      <div key={r.code || idx} className="flex justify-between items-center p-2 rounded-lg bg-gray-50 dark:bg-gray-900">
                        <div>
                          <p className="text-sm font-medium text-gray-800 dark:text-white">{r.name || '-'}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{r.code || '-'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-gray-800 dark:text-white">{safeNum(r.orders)} 單</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{fmtHkd(r.revenue)}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* WhatsApp Operations + Offers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* WhatsApp */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-red-600" /> WhatsApp 營運
                </h3>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-gray-800 dark:text-white">{(whatsapp.inbound ?? 0).toLocaleString('zh-HK')}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">入站</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-gray-800 dark:text-white">{(whatsapp.outbound ?? 0).toLocaleString('zh-HK')}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">出站</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-gray-800 dark:text-white">{(whatsapp.autoReplies ?? 0).toLocaleString('zh-HK')}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">自動回覆</p>
                  </div>
                </div>
                <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">關鍵字觸發次數</h4>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'orderConfirm', label: '訂單確認' },
                    { key: 'giftCode', label: '禮品碼' },
                    { key: 'friend', label: '成為推薦夥伴' },
                    { key: 'offer', label: '食肆優惠' },
                  ].map(k => (
                    <div key={k.key} className="flex justify-between p-2 rounded-lg bg-gray-50 dark:bg-gray-900 text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{k.label}</span>
                      <span className="font-bold text-gray-800 dark:text-white">{whatsapp.keywordTriggers?.[k.key] ?? 0}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Offers */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-red-600" /> 優惠成效
                </h3>
                {offers.length === 0 ? (
                  <p className="text-gray-400 dark:text-gray-500 text-center py-8">暫無優惠數據</p>
                ) : (
                  <div className="space-y-3">
                    {offers.map((offer) => {
                      const claims = safeNum(offer.claims);
                      const redeemed = safeNum(offer.redeemed);
                      const maxClaims = Math.max(1, ...offers.map(o => safeNum(o.claims)));
                      return (
                        <div key={offer.key || offer.title || Math.random()}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-700 dark:text-gray-300 font-medium">{offer.title || offer.key || '-'}</span>
                            <span className="text-gray-800 dark:text-white font-bold">{claims} 索取 / {redeemed} 兌換</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                            <div className="bg-green-500 h-2.5 rounded-full" style={{ width: `${(claims / maxClaims) * 100}%` }} />
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">兌換率 {claims > 0 ? safePct(redeemed, claims) : '0.00%'}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAnalytics;
